<template>
  <div class="binary-with-toggle-wrapper switch">
    <label :for="id">{{ label }}</label>
    <input
      type="checkbox"
      :id="id"
      v-model="value"
      @change="toggleOption"
      :disabled="disabled"
    />

    <div
      v-if="value"
      :class="{ active: isRightValueSelected }"
      :data-text="isRightValueSelected ? right.value : left.value"
    >
      <button
        :class="{ active: !isRightValueSelected }"
        @click="setSwitchValue(left.value)"
      >
        {{ left.display[language] }}
      </button>
      <button
        :class="{ active: isRightValueSelected }"
        @click="setSwitchValue(right.value)"
      >
        {{ right.display[language] }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { advancedOptionToRenderConfig } from '@/utils/advancedOptions'

export default {
  name: 'SwitchInput',

  props: {
    id: {
      type: String,
      required: true,
    },
    advanced_option: {
      required: true,
      type: String,
    },
    question_index: {
      required: true,
      type: Number,
    },
    questions_groups_index: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      checkboxValue: false,
      optionConfig: null,
      label: '',
      required: false,
      left: {},
      right: {},
    }
  },

  methods: {
    setSwitchValue(value) {
      this.$store.dispatch('changeAdvanced', {
        questionGroupIndex: this.questions_groups_index,
        questionIndex: this.question_index,
        advancedKey: this.advanced_option,
        value,
      })
    },

    toggleOption() {
      this.value ? this.setSwitchValue(this.left.value) : this.setSwitchValue(null)
    },
  },

  computed: {
    ...mapGetters(['questionGroups']),

    value: {
      get() {
        const value =
          this.questionGroups[this.questions_groups_index].questions[this.question_index]
            .advancedOptions[this.advanced_option] || ''

        return value
      },

      set(value) {
        this.$store.dispatch('changeAdvanced', {
          questionGroupIndex: this.questions_groups_index,
          questionIndex: this.question_index,
          advancedKey: this.advanced_option,
          value,
        })
      },
    },

    isRightValueSelected() {
      return this.value === this.right.value
    },

    disabled() {
      const currentOptions = this.questionGroups[this.questions_groups_index].questions[
        this.question_index
      ].advancedOptions

      const isDisabled = this?.optionConfig.disableChecker?.(currentOptions)

      return isDisabled
    },
  },

  created() {
    this.optionConfig = advancedOptionToRenderConfig[this.advanced_option]
    this.label = this.optionConfig.displayText[this.language]
    this.required = this.optionConfig.required

    const [left, right] = this.optionConfig.toggleValues

    this.left = left
    this.right = right
  },
}
</script>

<style scoped lang="scss">
.switch {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  letter-spacing: 0.3px;
  white-space: nowrap;
  color: $gray-10;
  .light & {
    color: $main;
  }
  label {
    margin-right: 10px;
    @include tipography(16);
    .light & {
      color: $main;
    }
  }
  div {
    margin-left: 20px;
    border: 1px solid $gray-5;
    border-radius: 50px;
    position: relative;
    transition: all 0.3s ease;
    &::after {
      content: attr(data-text);
      background: $green-2;
      border-radius: 50px;
      padding: 6px 30px;
      position: absolute;
      left: 3px;
      top: 3px;
      color: $transparent;
      transition: all 0.3s ease;
    }
    &.active {
      &::after {
        right: 3px;
        left: auto;
      }
    }
    button {
      position: relative;
      z-index: 2;
      padding: 8px 30px;
      background: none;
      border: none;
      cursor: pointer;
      border-radius: 50px;
      transition: all 0.3s ease;
      @include tipography(14);
      font-weight: bold;
      text-transform: uppercase;
      color: $gray-5;
      &.active {
        color: $white;
        &:hover {
          color: $gray-light-2;
        }
      }
    }
  }
  .light & {
    button {
      color: $gray-2;
      &:hover {
        color: $gray-8;
      }
    }
  }
}
</style>
